/* eslint-disable react/jsx-props-no-spreading */

import {
  Button,
  Flex,
  HStack,
  Stack,
  Text,
  chakra,
  useCheckbox,
  useCheckboxGroup,
} from '@chakra-ui/react';

import { Check } from '@/client/components/icons/ContinuIcons';
import type { NotificationCreateInput } from '@/client/services/api/graphql/gql/graphql';
import { useCheckSlackEnabled } from '@/client/services/hooks/integrations/useCheckSlackEnabled';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function CustomCheckbox(props: any) {
  const { t } = useTranslation();
  const { state, getCheckboxProps, getInputProps, getLabelProps, htmlProps } = useCheckbox(props);
  const { isChecked } = state;
  const { label, onEditClick, isInvalid } = props;

  return (
    <chakra.label
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      border="1px solid"
      borderColor={isInvalid ? 'error' : isChecked ? 'warmNeutral.700' : 'warmNeutral.200'}
      borderRadius="6px"
      backgroundColor={isChecked ? 'warmNeutral.0' : 'white'}
      paddingX={4}
      paddingY={3}
      cursor="pointer"
      {...htmlProps}
    >
      <HStack>
        <input {...getInputProps()} hidden />

        <Flex
          alignItems="center"
          justifyContent="center"
          border="2px solid"
          borderColor={isInvalid ? 'error' : 'warmNeutral.700'}
          borderRadius={4}
          width={4}
          height={4}
          {...getCheckboxProps()}
        >
          {isChecked && (
            <Flex
              width={4}
              height={4}
              backgroundColor="warmNeutral.700"
              justifyContent="center"
              alignItems="center"
            >
              <Check boxSize={2} color="white" />
            </Flex>
          )}
        </Flex>

        <Text fontSize="14px" fontWeight={500} color="baseBlack" {...getLabelProps()}>
          {label}
        </Text>
      </HStack>

      {isChecked && (
        <Button
          border="1px solid"
          borderColor="warmNeutral.200"
          borderRadius="8px"
          backgroundColor="white"
          fontSize="12px"
          fontWeight={600}
          paddingX={2}
          paddingY={3}
          color="baseBlack"
          _hover={{
            cursor: 'pointer',
            backgroundColor: 'warmNeutral.100',
          }}
          onClick={onEditClick}
        >
          {t('notifications.editMessage')}
        </Button>
      )}
    </chakra.label>
  );
}

interface AssignmentNotificationsTypeCheckboxGroupProps {
  onOpen: () => void;
  setMessageFormType: (type: 'email' | 'slack' | 'in_app') => void;
}

export default function AssignmentNotificationsMessagingTypeCheckboxGroup({
  onOpen,
  setMessageFormType,
}: AssignmentNotificationsTypeCheckboxGroupProps) {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<NotificationCreateInput>();
  const { channels } = watch();
  const { data } = useCheckSlackEnabled();

  const { getCheckboxProps } = useCheckboxGroup({
    defaultValue: channels,
  });

  const messageTypeOptions = useMemo(
    () => [
      {
        label: t('notifications.messaging.email'),
        value: 'email',
        enabled: true,
        onEditClick: () => {
          setMessageFormType('email');
          onOpen();
        },
      },
      {
        label: t('notifications.messaging.slack'),
        value: 'slack',
        enabled: data?.slackEnabled,
        onEditClick: () => {
          setMessageFormType('slack');
          onOpen();
        },
      },
      {
        label: t('notifications.messaging.inApp'),
        value: 'in_app',
        enabled: true,
        onEditClick: () => {
          setMessageFormType('in_app');
          onOpen();
        },
      },
    ],
    [data],
  );

  const hasError = !!errors.channels;

  return (
    <Stack spacing={6} marginTop={6}>
      <Text variant="createLabel" color={hasError ? 'error' : 'baseBlack'}>
        {t('notifications.messageType')}
      </Text>

      {messageTypeOptions.map((option) => {
        if (!option.enabled) return;

        return (
          <CustomCheckbox
            key={option.value}
            {...getCheckboxProps({
              label: option.label,
              value: option.value,
            })}
            isChecked={channels.includes(option.value)}
            isInvalid={hasError}
            onEditClick={option.onEditClick}
            onChange={() => {
              if (channels.includes(option.value)) {
                setValue(
                  'channels',
                  channels.filter((channel) => channel !== option.value),
                );
              } else {
                setValue('channels', [...channels, option.value]);
              }
            }}
          />
        );
      })}
    </Stack>
  );
}
